import PropTypes from 'prop-types';

import {
  Checkbox,
  Radio,
  handlePageClick,
  LoaderSkeleton,
  Pagination,
  usePaginatedItems,
} from '@utilities';

import { getOptionLabel } from '../../utilities/helpers';

import styles from './_index.module.scss';

const Answers = ({
  activeGroupedPrompt,
  activePrompt,
  activePromptAnswers,
  isLoading,
  onAnswerChange,
  results,
}) => {
  const maxValues = activeGroupedPrompt?.maxValues ?? activePrompt?.maxValues;
  const answersPerPage = 9;
  const { currentItems, itemsPerPage, pageCount, setOffset } = usePaginatedItems({
    items: results,
    itemsPerPage: answersPerPage,
  });
  const onPageChange = (event) => {
    handlePageClick({ event, items: results, itemsPerPage: answersPerPage, setOffset });
  };

  const handleAnswerChange = (newAnswer) => {
    if (maxValues === 1) {
      onAnswerChange(
        activeGroupedPrompt ? { [activeGroupedPrompt.promptKey]: [newAnswer] } : [newAnswer]
      );
      return;
    }

    if (activeGroupedPrompt) {
      const isSelected = activePromptAnswers.some(({ id }) => id === newAnswer.id);
      const updatedAnswers = isSelected
        ? activePromptAnswers.filter(({ id }) => id !== newAnswer.id)
        : [...activePromptAnswers, newAnswer];
      onAnswerChange({ [activeGroupedPrompt?.promptKey]: updatedAnswers });
    } else {
      const isSelected = activePromptAnswers.some(({ id }) => id === newAnswer.id);
      const updatedAnswers = isSelected
        ? activePromptAnswers.filter(({ id }) => id !== newAnswer.id)
        : [...activePromptAnswers, newAnswer];
      onAnswerChange(updatedAnswers);
    }
  };

  const isMaxSelected = !maxValues ? false : activePromptAnswers?.length >= maxValues;

  if (isLoading)
    return (
      <LoaderSkeleton>
        <rect height="15" rx="2" ry="2" width="200" x="0" y="0" />
        <rect height="15" rx="2" ry="2" width="175" x="0" y="25" />
        <rect height="15" rx="2" ry="2" width="225" x="0" y="50" />
        <rect height="15" rx="2" ry="2" width="200" x="0" y="75" />
        <rect height="15" rx="2" ry="2" width="225" x="0" y="100" />
      </LoaderSkeleton>
    );

  if (currentItems === null || currentItems?.length === 0) {
    return <div>No results found.</div>;
  }

  return (
    <div className={styles['answers']}>
      <div className={styles['answers-list']}>
        {currentItems?.map((answer, index) => {
          const isSelected = activePromptAnswers?.some?.(({ id }) => id === answer.id) || false;
          const optionAnswer = isSelected ? answer[0] : undefined;
          const value = optionAnswer || answer;

          return maxValues === 1 ? (
            <Radio
              isChecked={isSelected}
              key={`option-${index}`}
              label={getOptionLabel({ childLength: 45, length: 30, option: answer })}
              name={activePrompt?.id}
              onChange={() => handleAnswerChange(answer)}
              value={value}
            />
          ) : (
            <Checkbox
              className={isSelected ? styles['is-selected'] : ''}
              isChecked={isSelected}
              isDisabled={isMaxSelected && !isSelected}
              isInverseColor
              key={answer?.id || index}
              label={getOptionLabel({ childLength: 45, length: 30, option: answer })}
              name={answer?.name}
              onChange={() => handleAnswerChange(answer)}
              value={answer.attributeId}
            />
          );
        })}
      </div>
      <Pagination
        onPageChange={onPageChange}
        pageCount={pageCount}
        show={results?.length > itemsPerPage}
      />
    </div>
  );
};

Answers.propTypes = {
  activeGroupedPrompt: PropTypes.object,
  activePrompt: PropTypes.object.isRequired,
  activePromptAnswers: PropTypes.array,
  isLoading: PropTypes.bool,
  onAnswerChange: PropTypes.func,
  results: PropTypes.arrayOf(
    PropTypes.shape({
      content: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string,
    })
  ),
};

export default Answers;
